import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, CircularProgress, Stack, Tab, Tabs, Theme, Typography, useTheme } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useFormState } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./translations";

import { useGetServiceApiValuationPackageStatusQuery } from "@/api/enhancedGeneratedApi";
import { ValuationHubCoreModelRealEstateAddress } from "@/api/generatedApi";
import { SoneResponse } from "@/api/sprengConnectApi/types/oneClickController/response/SoneResponse";
import { CompletenessIndicator } from "@/components/completenessIndicator";
import { CustomIconButton } from "@/components/customIconButton";
import {
  buildingDataCompletionStatus,
  energyAndPhotovoltaicCompletionStatus,
  financialAndRentalInfoCompletionStatus,
  modernizationCompletionStatus,
  propertyImagesCompletionStatus,
} from "@/helpers/realEstateDataValidationAndCompletenessHelpers";
import { getAvailableTabs } from "@/helpers/realEstateRefinementHelpers";
import { useModuleContext } from "@/hooks/useModuleContext";
import { OneClickPropertyDetails } from "@/pages/oneClickPage/oneClickPageSlice";
import {
  type PaymentModalState,
  PaymentModelModal,
} from "@/pages/valuationPackage/valuationDetailsPage/PaymentModelModal";
import { clearCurrentValuation } from "@/pages/valuationPackage/valuationDetailsPage/currentValuationSlice";
import { useAppDispatch } from "@/store/hooks";
import { DataSources } from "@/types/oneClick";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";
import { trackPageView, trackTealiumEvent } from "@/utils/tracking/usage-tracking";
import { realEstateRefinementSchema } from "@/validation/realEstateRefinement";

import { CloseRefineRealEstateModal } from "./CloseRefineRealEstateModal";
import { STATUS_ICON_MAP, TAB_COMPONENT_MAP } from "./dataComponents";
import { DATA_GROUPS, DataGroupEnum, DataGroupType } from "./dataGroupsConstants";
import { TabHeaderWithAddress } from "./dataRefineTabs/TabHeaderWithAddress";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme, isValuationReport: boolean) => ({
  formContainer: {
    backgroundColor: "white",
    p: isValuationReport ? "74px 24px 0 24px" : "24px 24px 0 24px",
    display: "grid",
    gridTemplateRows: "auto auto 1fr auto",
    overflow: "hidden",
  },
  tabsHeader: { display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 4 },
  tabsListContainer: {
    borderBottom: 1,
    borderColor: "primary.main",
    minWidth: "650px",
  },
  tabContentContainer: {
    flex: 1,
    overflow: "hidden",
    py: 4,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  tabScrollableContainer: {
    flex: 1,
    overflow: "auto",
    minHeight: 0,
    "&::-webkit-scrollbar": {
      width: "10px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#D9D9D9",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.primary.main,
      borderRadius: "0",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.primary.main,
      borderRadius: "0",
    },
  },
  tabPanelContent: {
    paddingRight: "230px",
  },
  footerContainer: {
    display: "flex",
    justifyContent: "space-between",
    position: "sticky",
    bottom: 0,
    mt: "auto",
    p: "15px 0",
    zIndex: 1,
    backgroundColor: "white",
    borderTop: 1,
    borderColor: "primary.main",
  },
  buttonGroupContainer: {
    display: "flex",
    gap: "20px",
    alignItems: "center",
  },
  footerSelectedTabLabel: { fontSize: "14px", fontWeight: 700, width: "220px", textAlign: "center", flexShrink: 0 },
});

interface RefineRealEstateProps {
  address?: ValuationHubCoreModelRealEstateAddress | null;
  propertyDetails: PropertyInformation | OneClickPropertyDetails;
  setRefineData: (selectedDataGroup: DataGroupType | undefined) => void;
  initiallySelectedTab?: DataGroupType;
  setIsDataValid?: (isValid: boolean) => void;
  setPropertyDataSources?: (dataSources: DataSources) => void;
  setSoneValuation?: (soneResponse: SoneResponse | null) => void;
  updatePropertyDetails?: (
    formData: PropertyInformation,
    dirtyFields?: Record<string, object | boolean>,
  ) => unknown | Promise<unknown>;
  performSoneAvmValuation?: (id?: string) => void;
  isValuationDraft?: boolean;
  showCloseConfirmationOnCancel?: boolean;
  readonly?: boolean;
  valuationId?: string | null;
  useImagesList?: boolean;
  pendingImages?: Array<{
    id: string;
    file: File;
    preview: string;
    title: string;
  }>;
  setPendingImages?: React.Dispatch<
    React.SetStateAction<
      Array<{
        id: string;
        file: File;
        preview: string;
        title: string;
      }>
    >
  >;
}

const getDirtyFieldNames = (dirtyFields: Record<string, object | boolean>): string[] => {
  const propertyNames: string[] = [];

  const extractNames = (obj: Record<string, object | boolean>): void => {
    for (const [key, value] of Object.entries(obj)) {
      if (value === true) {
        propertyNames.push(key);
      } else if (typeof value === "object" && value !== null) {
        extractNames(value as Record<string, object | boolean>);
      }
    }
  };

  extractNames(dirtyFields);
  return propertyNames;
};

export const RefineRealEstate = ({
  address,
  propertyDetails,
  setRefineData,
  initiallySelectedTab,
  setIsDataValid,
  setPropertyDataSources,
  setSoneValuation,
  updatePropertyDetails,
  performSoneAvmValuation,
  isValuationDraft,
  showCloseConfirmationOnCancel,
  readonly,
  valuationId,
  useImagesList = false,
  pendingImages,
  setPendingImages,
}: RefineRealEstateProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });
  const { t: tCommon } = useTranslation();

  const theme = useTheme();
  const { isValuationPackage, isValuationReport, isOutlierShield } = useModuleContext();
  const styles = createStyles(theme, isValuationReport);
  const [selectedTab, setSelectedTab] = useState<DataGroupType>(
    initiallySelectedTab || DATA_GROUPS[0] || DataGroupEnum.generalInfo,
  );
  const [paymentModalState, setPaymentModalState] = useState<PaymentModalState>(null);
  const [isValuationSubmitting, setIsValuationSubmitting] = useState(false);
  const [isCloseRefineModalOpen, setIsCloseRefineModalOpen] = useState(false);
  const [isApplyChangesSubmitting, setIsApplyChangesSubmitting] = useState(false);
  const [focusErrorFieldPath, setFocusErrorFieldPath] = useState<string | null>(null);
  const [focusErrorFieldRequest, setFocusErrorFieldRequest] = useState(0);
  const formContainerRef = useRef<HTMLDivElement | null>(null);

  const { refetch: refetchQuotaStatus } = useGetServiceApiValuationPackageStatusQuery(undefined, {
    skip: !isValuationDraft,
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const initialFormData = {
    ...propertyDetails,
    energyAndPhotovoltaic: {
      ...propertyDetails.energyAndPhotovoltaic,
      energyCertificate: propertyDetails.energyAndPhotovoltaic.energyCertificate ?? null,
      energyEfficiencyHouseStandard: propertyDetails.energyAndPhotovoltaic.energyEfficiencyHouseStandard ?? null,
    },
  };

  const methods = useForm<PropertyInformation>({
    resolver: yupResolver(realEstateRefinementSchema),
    defaultValues: initialFormData,
    mode: "onChange",
    reValidateMode: "onChange",
    context: { objectType: initialFormData.generalInfo?.objectType },
  });

  const formValues = methods.watch();
  const { dirtyFields, isValid } = useFormState({ control: methods.control });
  const changedFieldNames = getDirtyFieldNames(dirtyFields);
  const hasChanges = changedFieldNames.length > 0;

  useEffect(() => {
    const completionStatus = buildingDataCompletionStatus(
      formValues.buildingData,
      formValues.generalInfo.objectType,
      formValues.generalInfo.buildingType,
    );
    if (formValues.buildingData.completionStatus !== completionStatus) {
      methods.setValue("buildingData.completionStatus", completionStatus, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.buildingData.constructionYear,
    formValues.buildingData.commercialArea,
    formValues.buildingData.commercialUnits,
    formValues.buildingData.livingArea,
    formValues.buildingData.construction,
    formValues.buildingData.rooms,
    formValues.buildingData.floorNumber,
    formValues.buildingData.numberOfFloors,
    formValues.buildingData.unitsNumber,
    formValues.buildingData.elevator,
    formValues.buildingData.garages,
    formValues.buildingData.outdoorParkingSpaces,
    formValues.buildingData.completionStatus,
    formValues.generalInfo.objectType,
    formValues.generalInfo.buildingType,
    methods,
  ]);

  useEffect(() => {
    const completionStatus = energyAndPhotovoltaicCompletionStatus(
      formValues.energyAndPhotovoltaic,
      formValues.buildingData.constructionYear,
    );
    if (formValues.energyAndPhotovoltaic.completionStatus !== completionStatus) {
      methods.setValue("energyAndPhotovoltaic.completionStatus", completionStatus, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.energyAndPhotovoltaic.energyCertificate,
    formValues.energyAndPhotovoltaic.energyEfficiencyHouseStandard,
    formValues.energyAndPhotovoltaic.issueDate,
    formValues.energyAndPhotovoltaic.certificateType,
    formValues.energyAndPhotovoltaic.finalEnergy,
    formValues.energyAndPhotovoltaic.primaryEnergy,
    formValues.energyAndPhotovoltaic.CO2Emissions,
    formValues.energyAndPhotovoltaic.photovoltaicAvailable,
    formValues.energyAndPhotovoltaic.constructionYear,
    formValues.energyAndPhotovoltaic.annualPowerYield,
    formValues.energyAndPhotovoltaic.completionStatus,
    formValues.buildingData.constructionYear,
    methods,
  ]);

  useEffect(() => {
    const completionStatus = modernizationCompletionStatus(
      formValues.modernization,
      formValues.buildingData.constructionYear,
    );
    if (formValues.modernization.completionStatus !== completionStatus) {
      methods.setValue("modernization.completionStatus", completionStatus, { shouldDirty: false });
    }
  }, [
    formValues.modernization.method,
    formValues.modernization.refurbishmentYear,
    formValues.modernization.class,
    formValues.modernization.cost,
    formValues.modernization.costDescription,
    formValues.modernization.completionStatus,
    formValues.buildingData.constructionYear,
    methods,
    formValues.modernization,
  ]);

  useEffect(() => {
    const completionStatus = propertyImagesCompletionStatus(formValues.propertyImages.images, pendingImages);
    if (formValues.propertyImages.completionStatus !== completionStatus) {
      methods.setValue("propertyImages.completionStatus", completionStatus, { shouldDirty: false });
    }
  }, [formValues.propertyImages.images, formValues.propertyImages.completionStatus, methods, pendingImages]);

  useEffect(() => {
    const completionStatus = financialAndRentalInfoCompletionStatus(formValues.financialAndRentalInfo);
    if (formValues.financialAndRentalInfo.completionStatus !== completionStatus) {
      methods.setValue("financialAndRentalInfo.completionStatus", completionStatus, { shouldDirty: false });
    }
  }, [
    formValues.financialAndRentalInfo.rent?.value,
    formValues.financialAndRentalInfo.completionStatus,
    methods,
    formValues.financialAndRentalInfo,
  ]);

  // OneClick and Outlier Shield do not support images
  const availableTabs: DataGroupType[] = getAvailableTabs(
    {
      ...formValues.generalInfo,
      selfUseSuitability: formValues.generalInfo.selfUseSuitability ?? false,
    },
    formValues.buildingData.constructionYear,
  )
    .filter((tab) => isValuationReport || isValuationPackage || useImagesList || tab !== DataGroupEnum.propertyImages)
    .filter((tab) => tab !== DataGroupEnum.financialAndRentalInfo);

  useEffect(() => {
    trackPageView(selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    if (!focusErrorFieldPath || !focusErrorFieldRequest) return;

    const frameId = requestAnimationFrame(() => {
      const inputSelector = `[name="${focusErrorFieldPath}"]`;
      const exactField = formContainerRef.current?.querySelector(inputSelector) as HTMLElement | null;
      const fallbackInvalidField = formContainerRef.current?.querySelector(
        '[aria-invalid="true"]',
      ) as HTMLElement | null;
      const target = exactField ?? fallbackInvalidField;

      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        target.focus();
        target.select();
        return;
      }

      target?.focus();
    });

    return (): void => cancelAnimationFrame(frameId);
  }, [focusErrorFieldPath, focusErrorFieldRequest, selectedTab]);

  useEffect(() => {
    if (setIsDataValid) setIsDataValid(isValid);
  }, [isValid, setIsDataValid]);

  const handleTabChange = (_event: React.SyntheticEvent, newTab: DataGroupType): void => {
    const currentValues = methods.getValues();
    methods.reset(currentValues, { keepValues: true, keepErrors: true, keepDirty: true });
    setSelectedTab(newTab);
  };

  const handleCloseRefinement = (): void => {
    setRefineData(undefined);
    if (isValuationDraft) {
      dispatch(clearCurrentValuation());
      navigate("/valuations");
    }
  };

  const handleRequestCloseRefinement = (): void => {
    if (showCloseConfirmationOnCancel) {
      setIsCloseRefineModalOpen(true);
      return;
    }

    if (isOutlierShield || !hasChanges) {
      handleCloseRefinement();
      return;
    }

    setIsCloseRefineModalOpen(true);
  };

  // saves draft in Valuation Package flow
  const handleSaveDraft = (): void => {
    if (!valuationId || hasChanges) {
      updatePropertyDetails && updatePropertyDetails(methods.getValues(), dirtyFields);
    }
    handleCloseRefinement();
  };

  const getFirstNestedErrorPath = (errors: unknown, parentPath = ""): string | undefined => {
    if (!errors || typeof errors !== "object") {
      return undefined;
    }

    for (const [key, value] of Object.entries(errors as Record<string, unknown>)) {
      const currentPath = parentPath ? `${parentPath}.${key}` : key;

      if (value && typeof value === "object") {
        if ("message" in value || "type" in value) {
          return currentPath;
        }

        const nestedPath = getFirstNestedErrorPath(value, currentPath);
        if (nestedPath) {
          return nestedPath;
        }
      }
    }

    return undefined;
  };

  const getFirstBlockingErrorPath = (errors: FieldErrors<PropertyInformation>): string | undefined => {
    for (const tab of availableTabs) {
      const tabErrors = errors[tab];
      if (!tabErrors) continue;

      const path = getFirstNestedErrorPath(tabErrors, tab);
      if (path) {
        return path;
      }
    }

    return undefined;
  };

  const handleValidationFailure = (errors: FieldErrors<PropertyInformation>): void => {
    const firstBlockingErrorPath = getFirstBlockingErrorPath(errors);
    if (!firstBlockingErrorPath) return;

    setIsCloseRefineModalOpen(false);

    const [tabName] = firstBlockingErrorPath.split(".");
    if (tabName && availableTabs.includes(tabName as DataGroupType)) {
      setSelectedTab(tabName as DataGroupType);
    }
    setFocusErrorFieldPath(firstBlockingErrorPath);
    setFocusErrorFieldRequest((current) => current + 1);
  };

  const handleApplyChanges = async (): Promise<void> => {
    if (isApplyChangesSubmitting) return;

    setIsApplyChangesSubmitting(true);

    const isFormValid = await methods.trigger(undefined, { shouldFocus: false });

    if (!isFormValid) {
      handleValidationFailure(methods.formState.errors);
      setIsApplyChangesSubmitting(false);
      return;
    }

    const formData = methods.getValues();

    const updatedSources = Object.fromEntries(
      changedFieldNames.map((propertyName) => [
        propertyName === "energyCertificate" ? "energyEfficiencyClass" : propertyName,
        "USER_INPUT",
      ]),
    );

    if (isValuationDraft) {
      const { data: freshUserStatus } = await refetchQuotaStatus();
      if (freshUserStatus?.authentication_source === "SHOPWARE") {
        setPaymentModalState(freshUserStatus.can_create_valuations ? "coinConfirmation" : "insufficientCoins");
      } else {
        const remainingCount = freshUserStatus?.remaining_valuations_count ?? 0;
        setPaymentModalState(remainingCount === 0 ? "quotaExhausted" : "quotaConfirmation");
      }
      setIsApplyChangesSubmitting(false);
    } else {
      if (updatePropertyDetails && hasChanges) {
        try {
          await Promise.resolve(updatePropertyDetails(formData, dirtyFields));
        } catch (error) {
          console.error("Failed to update property details before valuation:", error);
          setIsApplyChangesSubmitting(false);
          return;
        }
        const hasNonImageChanges = Object.keys(dirtyFields).some((key) => key !== "propertyImages");
        if (performSoneAvmValuation && hasNonImageChanges) {
          performSoneAvmValuation();
        }
        if (isValuationReport)
          trackTealiumEvent(
            {
              event_name: "save_object_data",
              event_product: "valuationpush",
            },
            { includeRealEstateId: true },
          );
      }
      if (setPropertyDataSources) setPropertyDataSources(updatedSources);
      setRefineData(undefined);
      if (setSoneValuation && hasChanges) {
        setSoneValuation(null);
      }
      setIsApplyChangesSubmitting(false);
    }
  };

  const handleValuationConfirm = (): void => {
    if (isValuationSubmitting) return;

    void (async (): Promise<void> => {
      setIsValuationSubmitting(true);
      try {
        const formData = methods.getValues();
        if (updatePropertyDetails) {
          try {
            await Promise.resolve(updatePropertyDetails(formData, dirtyFields));
          } catch (error) {
            console.error("Failed to update property details before valuation:", error);
            return;
          }
          const hasNonImageChanges = Object.keys(dirtyFields).some((key) => key !== "propertyImages");
          if (performSoneAvmValuation && (isValuationDraft || hasNonImageChanges)) {
            performSoneAvmValuation();
          }
          setRefineData(undefined);
        }
        setPaymentModalState(null);
      } finally {
        setIsValuationSubmitting(false);
      }
    })();
  };

  const handlePreviousGroup = (): void => {
    const currentIndex = availableTabs.indexOf(selectedTab);
    if (currentIndex > 0) {
      const previousGroup = availableTabs[currentIndex - 1] as DataGroupType;
      setSelectedTab(previousGroup);
    }
  };

  const handleNextGroup = (): void => {
    const currentIndex = availableTabs.indexOf(selectedTab as DataGroupEnum);
    if (currentIndex < availableTabs.length - 1) {
      const nextGroup = availableTabs[currentIndex + 1] as DataGroupType;
      setSelectedTab(nextGroup);
    }
  };

  const isFirstGroup = availableTabs.indexOf(selectedTab as DataGroupEnum) === 0;
  const isLastGroup = availableTabs.indexOf(selectedTab as DataGroupEnum) === availableTabs.length - 1;

  const TabComponent = TAB_COMPONENT_MAP[selectedTab];

  const getFormHeightOffset = (): number => {
    if (isValuationPackage) return 130;
    if (isValuationReport) return 182;
    return 160;
  };

  return (
    <>
      <FormProvider {...methods}>
        <Box
          ref={formContainerRef}
          component="form"
          onSubmit={(e) => e.preventDefault()}
          sx={{
            ...styles.formContainer,
            height: `calc(100vh - ${getFormHeightOffset()}px)`,
          }}
          data-cy="refine-data-form"
        >
          <Box sx={styles.tabsHeader}>
            <TabHeaderWithAddress label={t(`dataVerification.${selectedTab}`)} address={address} />
            <CustomIconButton
              sx={{ height: "36px", width: "40px" }}
              onClick={handleRequestCloseRefinement}
              data-cy="refine-data-cancel"
            >
              <CloseIcon sx={{ fontSize: "30px" }} />
            </CustomIconButton>
          </Box>

          <Box sx={{ pt: "30px", ...styles.tabsHeader }}>
            <Box sx={styles.tabsListContainer}>
              <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons={"auto"}>
                {availableTabs.map((tab, index) => (
                  <Tab
                    key={index}
                    value={tab}
                    label={
                      <Stack spacing={1} alignItems="center">
                        {
                          STATUS_ICON_MAP[
                            formValues[tab]?.completionStatus ??
                              (propertyDetails as PropertyInformation)[tab as keyof PropertyInformation]
                                ?.completionStatus ??
                              "notStarted"
                          ]?.icon
                        }
                        <Typography variant="subText" sx={{ fontSize: "12px" }}>
                          {t(`dataVerification.${tab}`)}
                        </Typography>
                      </Stack>
                    }
                  />
                ))}
              </Tabs>
            </Box>
            {!readonly && (
              <Stack direction="row" spacing={"20px"} alignItems={"center"}>
                <CompletenessIndicator realEstateState={formValues} size={50} />
                <Typography variant="subText" sx={{ fontSize: "12px" }}>
                  {t("dataVerification.informationCompleteness")}
                </Typography>
              </Stack>
            )}
          </Box>

          <Box sx={styles.tabContentContainer}>
            <Box sx={styles.tabScrollableContainer}>
              <Box sx={styles.tabPanelContent}>
                <TabComponent
                  address={address}
                  readonly={readonly}
                  isObjectTypeReadOnly={isValuationDraft !== undefined ? !isValuationDraft : undefined}
                  valuationId={valuationId}
                  useImagesList={useImagesList}
                  pendingImages={pendingImages}
                  setPendingImages={setPendingImages}
                />
              </Box>
            </Box>
          </Box>

          <Box sx={styles.footerContainer}>
            <Box sx={styles.buttonGroupContainer}>
              <Button
                variant="contained"
                color="secondary"
                sx={{ height: "fit-content" }}
                startIcon={<ArrowBackIosIcon />}
                disabled={isFirstGroup}
                onClick={handlePreviousGroup}
              >
                {tCommon("buttons.back").toLowerCase()}
              </Button>
              {!isLastGroup && (
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ height: "fit-content" }}
                  endIcon={<ArrowForwardIosIcon />}
                  disabled={isLastGroup}
                  onClick={handleNextGroup}
                  data-cy="refine-data-next"
                >
                  {tCommon("buttons.next")}
                </Button>
              )}
            </Box>
            <Box sx={styles.buttonGroupContainer}>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={handleApplyChanges}
                data-cy="refine-data-save"
                disabled={isApplyChangesSubmitting || (!isValuationDraft && !hasChanges)}
                endIcon={isApplyChangesSubmitting ? <CircularProgress size={20} /> : undefined}
              >
                {isValuationPackage || isValuationReport
                  ? t("dataVerification.implementValuation")
                  : t("dataVerification.applyData")}
              </Button>
            </Box>
          </Box>
        </Box>
      </FormProvider>
      <PaymentModelModal
        variant={paymentModalState}
        onCancel={() => {
          if (isValuationSubmitting) return;
          setPaymentModalState(null);
        }}
        onConfirm={handleValuationConfirm}
        coinsShopUrl={window.VITE_SHOPWARE_COINS_URL}
        isSubmitting={isValuationSubmitting}
      />
      <CloseRefineRealEstateModal
        open={isCloseRefineModalOpen}
        onCancel={() => {
          if (isApplyChangesSubmitting) return;
          setIsCloseRefineModalOpen(false);
        }}
        onSaveAndExit={handleSaveDraft}
        onDiscardAndExit={handleCloseRefinement}
        onApplyChanges={handleApplyChanges}
        canApplyChanges={hasChanges}
        isValuationAction={isValuationPackage || isValuationReport}
        isSubmitting={isApplyChangesSubmitting}
        variant={isValuationDraft ? "save" : "discard"}
      />
    </>
  );
};
